import React from 'react';
import { Typography, Chip, styled, SxProps, Theme } from '@mui/material';
import { DateTime } from 'luxon';
import { IntroRequestStatusEnum, RoleEnum } from '@src/lookup';
import { MoChip, MoChipColor } from '../../MoChip';
import { IntroRequestType } from '../types';

const StyledChip = styled(Chip)({
  fontSize: '14px',
  fontWeight: 600,
  padding: '8px',
  borderRadius: '20px',
  height: 'unset',
});

const IntroRequestStatusEnumToLabel = {
  [IntroRequestStatusEnum.WAITING_ON_USER]: 'Times sent',
  [IntroRequestStatusEnum.WAITING_ON_SUPERVISOR]: 'Times sent',
  [IntroRequestStatusEnum.SCHEDULED]: 'Call booked',
  [IntroRequestStatusEnum.ACCEPTED_BY_USER]: 'Match sent',
  [IntroRequestStatusEnum.ACCEPTED_BY_SUPERVISOR]: 'Match sent',
  [IntroRequestStatusEnum.ACCEPTED]: 'Matched',
  [IntroRequestStatusEnum.DECLINED_BY_USER]: 'Cancelled',
  [IntroRequestStatusEnum.DECLINED_BY_SUPERVISOR]: 'Cancelled',
  [IntroRequestStatusEnum.EXPIRED]: 'Expired',
  [IntroRequestStatusEnum.WAITING_ON_ADMIN_APPROVAL]: 'Finding new times',
  [IntroRequestStatusEnum.AVAILABILITY_REJECTED_BY_ADMIN]: 'Finding new times',
  [IntroRequestStatusEnum.AVAILABILITY_REJECTED_BY_EMPLOYER]: 'Finding new times',
};

type IntroRequestStatusChipType = {
  userId: number;
  introRequest: IntroRequestType;
  size?: 'sm' | 'md';
};

export default function IntroRequestStatusChip({
  userId,
  introRequest,
  size = 'md',
}: IntroRequestStatusChipType) {
  const otherParty =
    userId === introRequest.supervisor.id ? RoleEnum.SUPERVISEE : RoleEnum.SUPERVISOR;
  const { scheduledAt, introRequestStatusId } = introRequest;
  const label = IntroRequestStatusEnumToLabel[introRequestStatusId];

  const renderChip = (color: MoChipColor, sx?: SxProps<Theme>) =>
    size === 'sm' ? (
      <MoChip size="sm" color={color} label={label} />
    ) : (
      <StyledChip label={label} sx={sx} />
    );

  if (label === '-') return <span>{label}</span>;

  if (
    [
      IntroRequestStatusEnum.ACCEPTED,
      IntroRequestStatusEnum.DECLINED_BY_SUPERVISOR,
      IntroRequestStatusEnum.DECLINED_BY_USER,
      IntroRequestStatusEnum.EXPIRED,
    ].includes(introRequestStatusId)
  ) {
    return (
      <Typography variant={size === 'sm' ? 'body1' : 'body3'} color="text.tertiary">
        {label}
      </Typography>
    );
  }

  const waitingOnOtherUser =
    (introRequestStatusId === IntroRequestStatusEnum.WAITING_ON_SUPERVISOR &&
      otherParty === RoleEnum.SUPERVISOR) ||
    (introRequestStatusId === IntroRequestStatusEnum.WAITING_ON_USER &&
      otherParty === RoleEnum.SUPERVISEE) ||
    (introRequestStatusId === IntroRequestStatusEnum.ACCEPTED_BY_SUPERVISOR &&
      otherParty === RoleEnum.SUPERVISEE) ||
    (introRequestStatusId === IntroRequestStatusEnum.ACCEPTED_BY_USER &&
      otherParty === RoleEnum.SUPERVISOR);

  const waitingOnNewTimes = [
    IntroRequestStatusEnum.WAITING_ON_ADMIN_APPROVAL,
    IntroRequestStatusEnum.AVAILABILITY_REJECTED_BY_ADMIN,
    IntroRequestStatusEnum.AVAILABILITY_REJECTED_BY_EMPLOYER,
  ].includes(introRequestStatusId);

  if (waitingOnOtherUser || waitingOnNewTimes) {
    return renderChip('gray');
  }

  if (
    introRequestStatusId === IntroRequestStatusEnum.SCHEDULED &&
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    DateTime.local() < DateTime.fromISO(scheduledAt)
  )
    return renderChip('green', { backgroundColor: '#B0E1C0', color: '#066626' });

  return size === 'sm' ? (
    <MoChip size="sm" color="yellow" label="Action needed" />
  ) : (
    <StyledChip label="Action needed" sx={{ backgroundColor: '#FFE3AD', color: '#5C4300' }} />
  );
}
